import React from 'react';
import _ from 'lodash';
import MongoDocEditor from './MongoDocEditor';
import ModelContextEditor from './ModelContextEditor';
import { SingleImageUrlEditor } from '../common/images/SingleImageUrlEditor';
import TrimSpec from './trims/TrimSpec';
import LegoLabelsSelector from './LegoLabelsSelector';
import { MultipleImagesEditor } from '../common/editors/MultipleImagesEditor';
import NotesEditor from '../common/editors/NotesEditor';
import MiniatureIdEditor from '../miniatures/MiniatureIdEditor';

const GEN_SERVICE = '/services/data/models/gens';

function getGenSchema(obj) {
  const genIdentifier = obj.baseModelId + '_G' + obj.gen;

  return {
    baseModelId: (value, onChange) => <ModelContextEditor value={value ? { modelId: value } : null} onChange={ctx => onChange(ctx?.modelId)} />,
    specs: (value, onChange) => <ModelContextEditor value={value} onChange={ctx => onChange(ctx)} />,
    gen: null,
    genId: (value, onChange, obj) => <TrimSpec fullInfo spec={'genId'} value={`@${obj?.baseModelId}@G${obj?.gen}`} />,
    remappedToGenId: 'string',
    name: 'string',
    codenames: null,
    photo: (value, onChange) => <SingleImageUrlEditor folder={'model-gen-pictures'}
      category={'generation-picture'}
      value={value} defaultName={genIdentifier}
      onChange={onChange} />,
    fromYear: 'number',
    toYear: 'number',
    labels: (value, onChange) => <LegoLabelsSelector value={value} onChange={onChange} />,
    referencePhotos: (value, onChange) => <MultipleImagesEditor value={value} onChange={onChange} folder={'model-gen-ref-pictures'} forceJPG category={'generation-ref-pictures'} />,
    miniatureId: (value, onChange, o) => <MiniatureIdEditor value={value} onChange={onChange} defaultModelId={o?.baseModelId || obj.baseModelId} />,
    notes: (value, onChange) => <NotesEditor value={value} onChange={onChange} />,
  };
}

function prepareGenForSave(changedObj) {
  changedObj.genId = `@${changedObj.baseModelId}@G${changedObj.gen}`;
  if (changedObj.fromYear === undefined) delete changedObj.fromYear;
  if (changedObj.toYear === undefined) delete changedObj.toYear;
  return changedObj;
}

/**
 * Reusable generation document editor wrapping MongoDocEditor with the gen schema.
 *
 * @param {{ obj: Object, onSave: function, onCancel: function }} props
 */
export default function GenDocEditor({ obj, onSave, onCancel }) {
  const schema = getGenSchema(obj);

  const handleSave = (changedObj, ...args) => {
    prepareGenForSave(changedObj);
    return onSave(changedObj, ...args);
  };

  return (
    <MongoDocEditor
      extendSchema={schema}
      obj={obj}
      showFields={_.keys(schema)}
      onSave={handleSave}
      onCancel={onCancel}
    />
  );
}

/**
 * Opens a GenDocEditor in a modal for standalone usage (outside CRUDPage).
 * After a successful save, calls `onSaved` and closes the modal.
 */
export function openGenEditorModal(page, gen, { onSaved } = {}) {
  const svc = page.service(GEN_SERVICE);

  const onSave = async (changedObj, closeDialog) => {
    try {
      changedObj.updatedBy = page.getLoggedUserSignature?.() || undefined;
      const result = await svc.update(changedObj._id, changedObj);
      if (closeDialog) page.closeModal();
      if (onSaved) onSaved(result);
      return result;
    } catch (err) {
      page.handleError?.(err);
    }
  };

  const onCancel = () => page.closeModal();

  page.openModal(
    <div><GenDocEditor obj={gen} onSave={onSave} onCancel={onCancel} /></div>,
    { title: `Edit Gen ${gen.gen} – ${gen.baseModelId}` }
  );
}
